import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  createYowanBlogAuthorPost,
  getYowanApiFieldErrorMessage,
  getYowanAdminMutationErrorMessage,
} from '../app/yowanContentClient';
import {
  AdminContentEditorFields,
  AdminPageHeader,
  useBlogAuthorContentMediaOptions,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import {
  BlogAuthorGuardNotice,
  BlogAuthorShell,
  useBlogAuthorGuard,
} from './BlogAuthorDashboardPage';
const { yowanPageContent } = require('./pageContent');

const BlogAuthorPostCreatePage = ({ guardOptions }) => {
  const content = yowanPageContent['/blog-author/posts/new'];
  const navigate = useNavigate();
  const preservedDomainSearch = usePreservedDomainSearch();
  const guard = useBlogAuthorGuard(guardOptions);
  const canPublish = Boolean(guard.currentUser?.canPublishOwnBlogPosts || guard.currentUser?.canAccessAdmin);
  const {
    mediaAssetOptions,
    mediaAssetLoading,
    mediaAssetError,
  } = useBlogAuthorContentMediaOptions();
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: content?.form.defaults,
  });

  if (guard.status !== 'ready') {
    return <BlogAuthorGuardNotice notice={guard.notice} />;
  }

  if (!content) {
    return null;
  }

  const onSubmit = async (formValues) => {
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await createYowanBlogAuthorPost(formValues, { canPublish });
      navigate(appendYowanPreviewQueryIfNeeded('/blog-author/posts', preservedDomainSearch));
    } catch (error) {
      const slugMessage = getYowanApiFieldErrorMessage(error, ['slug']);

      if (slugMessage) {
        setError('slug', { type: 'server', message: slugMessage });
        setSubmitMessage('slugを確認してください。');
      } else {
        setSubmitMessage(getYowanAdminMutationErrorMessage(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BlogAuthorShell activeNavHref="/blog-author/posts" routeKey="/blog-author/posts/new">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/blog-author/posts', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />

      <form className="yowan-admin-form yowan-blog-author-post-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        <AdminContentEditorFields
          content={content}
          register={register}
          errors={errors}
          showSlugField
          slugRequired={false}
          showPublishControls={canPublish}
          showNotifyOnPublishField={false}
          showMediaFields
          mediaAssetOptions={mediaAssetOptions}
          mediaAssetLoading={mediaAssetLoading}
          mediaAssetError={mediaAssetError}
        />
        {!canPublish ? (
          <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
            {content.form.noPublishPermissionHelpText}
          </p>
        ) : null}
        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting}>
            {content.form.submitLabel}
          </button>
        </div>
        {isSubmitting ? (
          <p className="yowan-admin-form__message yowan-admin-form__message--pending">
            <LoadingSpinner inline size="sm" />
            <span>{content.form.pendingMessage}</span>
          </p>
        ) : submitMessage ? (
          <p className="yowan-admin-form__message">{submitMessage}</p>
        ) : null}
      </form>
    </BlogAuthorShell>
  );
};

export default BlogAuthorPostCreatePage;
